import React, { useState, useEffect, useMemo } from 'react';
import { apiService } from '../services/api'; 
import './style/OrgAssignmentModal.css'; 

const OrgAssignmentModal = ({ user, allOrganisations, onClose, onSave }) => {
  const [assignedIds, setAssignedIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Default: all collapsed
  const [expandedTypes, setExpandedTypes] = useState({});

  useEffect(() => {
    if (user) {
      fetchAssignments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchAssignments = async () => {
    try {
      const response = await apiService.getUserAssignedOrgs(user.id);
      setAssignedIds(response.map(org => org.id));
    } catch (error) {
      console.error("Failed to fetch assignments", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = (orgId) => {
    setAssignedIds(prev => {
      if (prev.includes(orgId)) {
        return prev.filter(id => id !== orgId);
      } else {
        return [...prev, orgId];
      }
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await apiService.updateUserAssignedOrgs(user.id, assignedIds);
      onSave();
    } catch (error) {
      alert("Failed to save assignments: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  // --- UPDATED: Added .sort() here ---
  const availableOrgs = useMemo(() => {
    return allOrganisations
      .filter(o => o.id !== user.organisationId)
      .filter(o => o.name.toLowerCase().includes(searchTerm.toLowerCase()))
      .sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically by name
  }, [allOrganisations, user.organisationId, searchTerm]);

  const groupedAvailableOrgs = useMemo(() => {
    return availableOrgs.reduce((groups, org) => {
      const type = org.type || 'Other';
      if (!groups[type]) groups[type] = [];
      groups[type].push(org);
      return groups;
    }, {});
  }, [availableOrgs]);

  const handleSelectAllType = (e, orgsInType) => {
    e.stopPropagation(); 
    const idsInType = orgsInType.map(o => o.id);
    const allSelected = idsInType.every(id => assignedIds.includes(id));

    if (allSelected) {
      setAssignedIds(prev => prev.filter(id => !idsInType.includes(id)));
    } else {
      setAssignedIds(prev => [...new Set([...prev, ...idsInType])]);
    }
  };

  const toggleExpand = (type) => {
    setExpandedTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const renderSelectionSummary = (orgsInType) => {
    const selectedInGroup = orgsInType.filter(o => assignedIds.includes(o.id));
    const count = selectedInGroup.length;
    const total = orgsInType.length;

    if (count === 0) return <span className="oam-summary-none">None selected</span>;
    if (count === total) return <span className="oam-summary-all">All {orgsInType[0].type}s selected</span>;

    const names = selectedInGroup.map(o => o.name);
    return (
      <span className="oam-summary-partial">
        {names.join(', ')}
      </span>
    );
  };

  return (
    <div className="oam-overlay">
      <div className="oam-container">
        
        <div className="oam-header">
          <h3 className="oam-title">Assign Organisations: {user.username}</h3>
          <button onClick={onClose} className="oam-close-btn">×</button>
        </div>
        
        <div className="oam-body">
          <div className="oam-search-bar">
            <input 
              type="text" 
              placeholder="Search organisations..." 
              className="oam-search-input" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {isLoading ? (
            <div className="oam-loading-state">Loading assignments...</div>
          ) : (
            <div className="oam-rows-container">
              {Object.keys(groupedAvailableOrgs).length === 0 ? (
                 <div className="oam-empty-state">No organisations found.</div>
              ) : (
                Object.keys(groupedAvailableOrgs).map(type => {
                  const orgsInType = groupedAvailableOrgs[type];
                  const idsInType = orgsInType.map(o => o.id);
                  const allSelected = idsInType.length > 0 && idsInType.every(id => assignedIds.includes(id));
                  const isExpanded = expandedTypes[type];

                  return (
                    <div key={type} className="oam-row-group">
                      {/* --- Accordion Header --- */}
                      <div className="oam-row-header" onClick={() => toggleExpand(type)}>
                        <div className="oam-header-top">
                          <div className="oam-header-title">
                            <span className={`oam-chevron ${isExpanded ? 'expanded' : ''}`}>▶</span>
                            <span className="oam-type-name">{type}</span>
                            <span className="oam-count-badge">{orgsInType.length}</span>
                          </div>
                          
                          <div className="oam-header-actions">
                            <label className="oam-select-all-label" onClick={(e) => e.stopPropagation()}>
                              <input 
                                type="checkbox"
                                checked={allSelected}
                                onChange={(e) => handleSelectAllType(e, orgsInType)}
                                className="oam-checkbox-input"
                              />
                              <span className="oam-select-all-text">Select All</span>
                            </label>
                          </div>
                        </div>

                        <div className="oam-header-summary">
                           {renderSelectionSummary(orgsInType)}
                        </div>
                      </div>

                      {/* --- Accordion Body --- */}
                      {isExpanded && (
                        <div className="oam-row-content">
                          <div className="oam-grid-list">
                            {orgsInType.map(org => {
                              const isSelected = assignedIds.includes(org.id);
                              return (
                                <div 
                                  key={org.id} 
                                  className={`oam-grid-item ${isSelected ? 'selected' : ''}`}
                                  onClick={() => handleToggle(org.id)} 
                                >
                                  <input 
                                    type="checkbox" 
                                    checked={isSelected}
                                    onChange={() => {}} 
                                    className="oam-checkbox-input"
                                  />
                                  <span className="oam-org-name">{org.name}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

          <div className="oam-actions">
            <div className="oam-summary-text">
              Total Assigned: <strong>{assignedIds.length}</strong>
            </div>
            <div className="oam-btn-group">
              <button className="oam-btn oam-btn-cancel" onClick={onClose} disabled={isSaving}>
                Cancel
              </button>
              <button className="oam-btn oam-btn-save" onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Assignments'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrgAssignmentModal;
